import { COLUMN_VERTICAL_ALIGN_VALUES, type ColumnsBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { ColumnWidthsField } from './ColumnWidthsField';
import { useEditorStore } from '../../stores/editorStore';
import { Switch } from '../ui/Switch';
import { Field } from '../ui/Field';

interface ColumnsBlockInspectorProps {
  block: ColumnsBlock;
  path: DragPath;
}

export function ColumnsBlockInspector({ block, path }: ColumnsBlockInspectorProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);

  const attrs = block.attrs;
  const set = (patch: Partial<ColumnsBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...attrs, ...patch } }, path);
  };

  return (
    <>
      <Field label="Vertical Align">
        <select
          className="field-input"
          value={attrs.verticalAlign}
          onChange={(e) =>
            set({ verticalAlign: e.target.value as ColumnsBlock['attrs']['verticalAlign'] })
          }
        >
          {COLUMN_VERTICAL_ALIGN_VALUES.map((option) => (
            <option key={option} value={option}>
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </option>
          ))}
        </select>
      </Field>

      <div>
        <Switch
          label="Wrap on mobile"
          checked={block.attrs.shouldWrapOnMobile}
          onChange={(val) =>
            updateBlock(
              block.id,
              {
                attrs: {
                  ...block.attrs,
                  shouldWrapOnMobile: val,
                },
              },
              path,
            )
          }
        />
        <p className="text-muted mt-1 text-xs">
          Off keeps the columns side by side at every width. That is stated inline, so it holds
          everywhere; on is a rule for narrow viewports, and a client that applies no media queries
          simply shows the columns instead of stacking them.
        </p>
      </div>

      <div>
        <label className="field-label">Number of columns</label>
        <div className="flex gap-1">
          {[2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                const existing = (block.attrs.columnBlocks as unknown[][] | undefined) ?? [];
                const columnBlocks = Array.from({ length: n }, (_, i) => existing[i] ?? []);
                updateBlock(
                  block.id,
                  { attrs: { ...block.attrs, columns: n, columnBlocks } },
                  path,
                );
              }}
              className={`flex-1 rounded border py-1.5 text-xs transition-colors ${
                block.attrs.columns === n
                  ? 'bg-accent border-accent text-accent-ink shadow-sm'
                  : 'border-line text-muted hover:bg-bg-soft'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="text-muted mt-1 text-xs">
          Add and edit content inside each column on the canvas.
        </p>
      </div>

      <div>
        <label className="field-label" htmlFor="columns-gap">
          Gap between columns
        </label>
        <input
          id="columns-gap"
          type="text"
          className="field-input font-mono"
          placeholder="2rem"
          value={block.attrs.gap ?? ''}
          onChange={(e) =>
            updateBlock(
              block.id,
              {
                attrs: { ...block.attrs, gap: e.target.value },
              },
              path,
            )
          }
        />
      </div>

      <ColumnWidthsField
        // Re-seeded from the block whenever the row it describes is
        // replaced — a different block, another column count, or an
        // undo — so the fields never show a layout that is not stored.
        key={`${block.id}:${block.attrs.columns ?? 1}:${JSON.stringify(
          block.attrs.columnWidths ?? [],
        )}`}
        columnWidths={block.attrs.columnWidths ?? []}
        colCount={block.attrs.columns ?? 1}
        onCommit={(next) =>
          updateBlock(block.id, { attrs: { ...block.attrs, columnWidths: next } }, path)
        }
      />
    </>
  );
}
